import { useApiQuery } from "@/api";
import {
    getCompanyConnectors,
    CompanyConnector,
    Account,
    ConnectorStatus,
} from "@/entities/Company";
import { formatDate } from "@/utils/misc/format";
import clsx from "clsx";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import { AllCommunityModule, ColDef, ModuleRegistry } from "ag-grid-community";
import { investmentMemoGridTheme } from "../../apps/AdminBetaApp/sections/companies/InvestmentMemo/components/agGridTheme";

ModuleRegistry.registerModules([AllCommunityModule]);

const getStatusTooltip = (status: string): string => {
    const tooltips: Record<string, string> = {
        active: "We got valid credentials with all the needed scopes to get data",
        disconnected:
            "The client disconnected the connection or we lost the credentials. Credentials are not valid anymore, we need the client to reconnect.",
        deauthorized:
            "The client deauthorized us from their application. Credentials are not valid anymore, we need the client to reconnect.",
        authenticating:
            "The client started the connection but didn't finish it",
        pending: "The client started the connection but didn't finish it",
    };
    return tooltips[status] || "Provides information about credentials status";
};

const getHealthTooltip = (health: string): string => {
    const tooltips: Record<string, string> = {
        healthy: "We pulled all the latest data",
        pending: "We are waiting for data to be pulled the first time",
        initial_pull_in_progress: "The first data pull is in progress",
        pull_in_progress: "Data pull is in progress",
        critical: "The last data pull failed",
        recovering: "The last data pull failed but was successfully retried",
        stale: "The last data pull was more than 7 days ago",
    };
    return tooltips[health] || "Provides information about data pull status";
};

export function ConnectivityTable({
    companyId,
    showActiveOnly = false,
}: {
    companyId: string;
    showActiveOnly?: boolean;
}) {
    const { data: connectors, isLoading } = useApiQuery(getCompanyConnectors, {
        params: { uuid: companyId },
    });

    const filteredConnectors = useMemo(() => {
        if (!connectors) return [];
        if (!showActiveOnly) return connectors;
        return connectors.filter(
            (connector) => connector.status === ConnectorStatus.Active,
        );
    }, [connectors, showActiveOnly]);

    const tableHeight = useMemo(() => {
        const rowCount = filteredConnectors.length;
        const headerHeight = 42;
        const rowHeight = 80;
        const calculatedHeight = headerHeight + rowCount * rowHeight;
        const maxHeight = 400;
        return Math.min(calculatedHeight, maxHeight);
    }, [filteredConnectors.length]);

    const columnDefs = useMemo<ColDef<CompanyConnector>[]>(
        () => [
            {
                field: "connectorType",
                headerName: "Service",
                headerClass: "heading-s",
                flex: 1.5,
                minWidth: 180,
                suppressHeaderMenuButton: true,
                cellRenderer: ({
                    value,
                    data,
                }: {
                    value: string;
                    data: CompanyConnector;
                }) => {
                    const connectorIdentifier = data.meta
                        ?.connectorIdentifier ?? {
                        institutionName: "",
                        accounts: [],
                    };
                    const { institutionName, accounts = [] } =
                        connectorIdentifier;
                    const accountNames = accounts
                        ? `account${accounts.length > 1 ? "s" : ""} ending ${accounts
                              .map((account: Account) => account.accountNumber)
                              .join(", ")}`
                        : "";
                    const accountLabel = institutionName || data.name;
                    const formattedType = value
                        ? value.charAt(0).toUpperCase() + value.slice(1)
                        : "";

                    return (
                        <div className="flex flex-col py-1">
                            <p>{formattedType}</p>
                            <p className="text-xs text-slate-400">
                                {accountLabel}
                            </p>
                            {accounts.length > 0 && (
                                <p className="text-xs text-slate-400">
                                    {`${accounts.length} account${
                                        accounts.length > 1 ? "s" : ""
                                    } (${accountNames})`}
                                </p>
                            )}
                        </div>
                    );
                },
            },
            {
                field: "createdAt",
                headerName: "Created at",
                headerClass: "heading-s",
                flex: 1,
                minWidth: 150,
                cellStyle: {
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.4",
                },
                valueFormatter: ({ value }) => {
                    return formatDate(value, { hideTime: true });
                },
            },
            {
                field: "status",
                headerName: "Status",
                headerClass: "heading-s",
                headerTooltip: "Provides information about credentials status",
                flex: 1,
                minWidth: 140,
                cellStyle: {
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.4",
                },
                tooltipValueGetter: ({ value }) => getStatusTooltip(value),
                cellRenderer: ({ value }: { value: string }) => {
                    const statusColors = {
                        active: "bg-green-200",
                        pending: "bg-orange-200",
                        disconnected: "bg-red-200",
                        deauthorized: "bg-gray-200",
                        authenticating: "bg-blue-200",
                    };
                    const colorClass =
                        statusColors[value as keyof typeof statusColors] ||
                        "bg-gray-200";
                    return (
                        <div
                            className={clsx(
                                "lozenge w-max rounded-xl px-2 py-1 text-brand-900",
                                colorClass,
                            )}
                        >
                            {value}
                        </div>
                    );
                },
            },
            {
                field: "healthStatus",
                headerName: "Health",
                headerClass: "heading-s",
                headerTooltip: "Provides information about data pull status",
                flex: 1,
                minWidth: 150,
                cellStyle: {
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.4",
                },
                tooltipValueGetter: ({ value }) => getHealthTooltip(value),
                cellRenderer: ({ value }: { value: string }) => {
                    const healthColors = {
                        healthy: "bg-green-200",
                        pending: "bg-orange-200",
                        critical: "bg-red-200",
                        recovering: "bg-orange-200",
                        stale: "bg-gray-200",
                        initial_pull_in_progress: "bg-blue-200",
                        pull_in_progress: "bg-blue-200",
                    };
                    const colorClass =
                        healthColors[value as keyof typeof healthColors] ||
                        "bg-gray-200";
                    return (
                        <div
                            className={clsx(
                                "lozenge w-max rounded-xl px-2 py-1 text-brand-900",
                                colorClass,
                            )}
                        >
                            {value?.replace(/_/g, " ")}
                        </div>
                    );
                },
            },
            {
                field: "lastSuccessPullAt",
                headerName: "Last Pull",
                headerClass: "heading-s",
                flex: 0.8,
                minWidth: 140,
                cellStyle: {
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.4",
                },
                valueFormatter: ({ value }) => {
                    if (!value) return "Never";

                    return formatDate(value, { hideTime: true });
                },
            },
        ],
        [],
    );

    const defaultColDef = useMemo<ColDef>(
        () => ({
            resizable: true,
            sortable: false,
            filter: false,
            wrapText: true,
            autoHeight: true,
            suppressHeaderMenuButton: true,
            suppressCellFlash: true,
            animateRows: false,
            cellStyle: {
                display: "flex",
                alignItems: "center",
                whiteSpace: "normal",
                wordWrap: "break-word",
                lineHeight: "1.4",
            },
        }),
        [],
    );

    if (isLoading) {
        return (
            <div className="rounded-[14px]-xl h-64 animate-pulse bg-slate-800" />
        );
    }

    if (!connectors || connectors.length === 0) {
        return (
            <div
                className={clsx(
                    "h-32 bg-slate-800/50",
                    "border border-slate-700",
                    "flex items-center justify-center rounded-xl",
                )}
            >
                <p className="text-slate-400">No connectors configured</p>
            </div>
        );
    }

    return (
        <>
            {filteredConnectors.length === 0 ? (
                <div
                    className={clsx(
                        "h-32 bg-slate-800/50",
                        "border border-slate-700",
                        "flex items-center justify-center rounded-xl",
                    )}
                >
                    <p className="text-slate-400">No active connectors found</p>
                </div>
            ) : (
                <div
                    className={clsx("ag-theme-quartz-dark rounded")}
                    style={{ height: `${tableHeight}px` }}
                >
                    <AgGridReact<CompanyConnector>
                        theme={investmentMemoGridTheme}
                        rowData={filteredConnectors}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        suppressAnimationFrame={true}
                        suppressRowTransform={true}
                        animateRows={false}
                    />
                </div>
            )}
        </>
    );
}
